/**
 * Theme API Client
 * Handles fetching organization branding from the API with caching and authentication
 */

import axios from 'axios';
import { auth } from '../firebase';
import themeCache from './theme-cache';
import themeErrorHandler from './theme-error-handler';
import performanceMonitor from '../utils/performance-monitor';
import { retryWithBackoff, getRetryConfig } from '../utils/retry-utils';

// API configuration (will use environment-specific base URL)
const getApiBaseUrl = () => {
  // Use environment-specific URL from firebase config
  const env = process.env.NODE_ENV || 'development';

  // For local development
  if (env === 'development' || window.location.hostname === 'localhost') {
    return 'http://localhost:8080'; // Adjust based on your API setup
  }

  // For production, use relative path or full URL
  return window.location.origin;
};

const API_BASE_URL = getApiBaseUrl();
const BRANDING_ENDPOINT = '/api/v1/workbench/urls';
const REQUEST_TIMEOUT = 10000; // 10 seconds

/**
 * ThemeApiClient class for fetching organization branding
 */
class ThemeApiClient {
  constructor(config = {}) {
    this.config = {
      apiBaseUrl: API_BASE_URL,
      timeout: REQUEST_TIMEOUT,
      retryAttempts: 2,
      retryDelay: 1000,
      useCache: true,
      ...config
    };

    this.cache = themeCache;
  }

  /**
   * Get authentication token from Firebase
   *
   * @returns {Promise<string|null>} The auth token or null
   */
  async getAuthToken() {
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        const token = await currentUser.getIdToken();
        return token;
      }
      return null;
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  }

  /**
   * Fetch branding data from API
   *
   * @param {string} slug - The organization slug
   * @param {boolean} skipCache - Skip cache and force API call
   * @returns {Promise<object|null>} The branding data or null
   */
  async fetchBranding(slug, skipCache = false) {
    const endMeasure = performanceMonitor.startMeasure('theme-api-fetch');

    try {
      // Check cache first (unless skipCache is true)
      if (this.config.useCache && !skipCache) {
        const cached = this.cache.get(slug);
        if (cached) {
          console.debug('Theme cache hit for:', slug);
          performanceMonitor.recordMetric('theme-cache-hit', endMeasure());
          return cached.branding;
        }
      }

      // Fetch from API
      const branding = await this.fetchFromApi(slug);

      // Cache the result
      if (this.config.useCache && branding) {
        this.cache.set(slug, branding);
      }

      endMeasure();
      return branding;
    } catch (error) {
      endMeasure();

      // Handle error with error handler
      const errorResult = themeErrorHandler.handleError(error, {
        context: 'api-fetch',
        domain: slug
      });

      console.error('Error fetching branding:', errorResult.message);

      // Try to return stale cache if strategy allows
      if (errorResult.useCache) {
        const cached = this.cache.get(slug);
        if (cached) {
          console.warn('Using stale cache due to API error');
          return cached.branding;
        }
      }

      throw error;
    }
  }

  /**
   * Fetch branding from API with retry logic
   *
   * @param {string} slug - The organization slug
   * @returns {Promise<object>} The branding data
   */
  async fetchFromApi(slug) {
    const retryConfig = getRetryConfig('api');

    return retryWithBackoff(async () => {
      // Get auth token
      const token = await this.getAuthToken();

      // Prepare request
      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await axios.post(
        `${this.config.apiBaseUrl}${BRANDING_ENDPOINT}`,
        { slug },
        {
          headers,
          timeout: this.config.timeout
        }
      );

      // Transform API response to internal format
      return this.transformResponse(response.data);
    }, retryConfig);
  }

  /**
   * Transform API response to internal branding format
   *
   * @param {object} apiResponse - The API response
   * @returns {object} The transformed branding data
   */
  transformResponse(apiResponse) {
    // API returns 8 color fields + images
    return {
      // Colors
      primary_color: apiResponse.primary_color || '',
      secondary_color: apiResponse.secondary_color || '',
      tertiary_color: apiResponse.tertiary_color || '',
      color1: apiResponse.color1 || '',
      color2: apiResponse.color2 || '',
      color3: apiResponse.color3 || '',
      color4: apiResponse.color4 || '',
      color5: apiResponse.color5 || '',

      // Images
      logo_img_url: apiResponse.logo_img_url || '',
      background_img_url: apiResponse.background_img_url || '',

      // Metadata
      workbench_version: apiResponse.workbench_version || '',
      workbench_name: apiResponse.workbench_name || '',
      org_guid: apiResponse.org_guid || null,
      error: apiResponse.error || ''
    };
  }

  /**
   * Invalidate cache for a specific domain
   *
   * @param {string} slug - The organization slug
   */
  invalidateCache(slug) {
    this.cache.delete(slug);
  }

  /**
   * Clear all cached themes
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Get cache info
   *
   * @returns {object} Cache statistics
   */
  getCacheInfo() {
    return this.cache.getCacheInfo();
  }

  /**
   * Refresh theme by fetching from API and updating cache
   *
   * @param {string} slug - The organization slug
   * @returns {Promise<object>} The fresh branding data
   */
  async refreshBranding(slug) {
    return this.fetchBranding(slug, true);
  }

  /**
   * Helper to delay execution
   *
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise} Promise that resolves after delay
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Export singleton instance
export default new ThemeApiClient();

// Export class for custom instances
export { ThemeApiClient };
